'use client';

import { Button } from '@/components/ui/Button';
import { getTranslatedStoreValue } from '@/lib/stores/utils';
import clsx from 'clsx';
import Image from 'next/image';
import type { ReactNode } from 'react';

import styles from './BookAppointmentModal.module.scss';
import { AppointmentFormStepFields } from './AppointmentFormStepFields';
import type { SelectedBoutique } from './types';
import { useAppointmentFormStep } from './useAppointmentFormStep';

export interface AppointmentFormStepProps {
  boutique: SelectedBoutique | null;
  productSku?: string;
  productName?: string;
  countryCode: string;
  localeSlug: string;
  languageCode: string;
  /** Page layout renders the hero outside the form step. */
  hideHero?: boolean;
  /** Page layout constrains form width to match boutique appointment Figma. */
  layout?: 'modal' | 'page';
  /** Unified page: retailer dropdowns rendered above the calendar. */
  retailerSection?: ReactNode;
  onBack: () => void;
  onSuccess: (fullName: string) => void;
}

interface AppointmentFormBoutiqueHeroProps {
  boutique: SelectedBoutique;
  displayName: string;
  onBack: () => void;
  changeStoreLabel: string;
}

function AppointmentFormBoutiqueHero({
  boutique,
  displayName,
  onBack,
  changeStoreLabel,
}: Readonly<AppointmentFormBoutiqueHeroProps>) {
  return (
    <div className={styles.boutiqueHero}>
      {boutique.imageUrl ? (
        <Image
          src={boutique.imageUrl}
          alt=""
          fill
          className={styles.boutiqueHeroImage}
          sizes="(max-width: 768px) 100vw, 750px"
        />
      ) : null}
      <div className={styles.boutiqueHeroOverlay}>
        <h3 className={styles.boutiqueHeroName}>{displayName}</h3>
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          {changeStoreLabel}
        </Button>
      </div>
    </div>
  );
}

function AppointmentFormProductBanner({
  productSku,
  productName,
}: Readonly<{ productSku?: string; productName: string }>) {
  return (
    <div className={styles.productBanner}>
      <p className={styles.productSku}>{productSku}</p>
      <p className={styles.productName}>{productName}</p>
    </div>
  );
}

/**
 * Appointment date/time selection and contact form step.
 */
export function AppointmentFormStep({
  boutique,
  productSku,
  productName,
  countryCode,
  localeSlug,
  languageCode,
  hideHero = false,
  layout = 'modal',
  retailerSection,
  onBack,
  onSuccess,
}: Readonly<AppointmentFormStepProps>) {
  const form = useAppointmentFormStep({
    boutique,
    productSku,
    countryCode,
    localeSlug,
    languageCode,
    layout,
    onSuccess,
  });

  const boutiqueDisplayName = boutique
    ? getTranslatedStoreValue(boutique.store, 'name', localeSlug)
    : '';
  const showHero = !hideHero && boutique;

  return (
    <div className={clsx(styles.wrapper, form.isPageLayout && styles.wrapperPage)}>
      {productName ? (
        <AppointmentFormProductBanner productSku={productSku} productName={productName} />
      ) : null}

      {showHero ? (
        <AppointmentFormBoutiqueHero
          boutique={boutique}
          displayName={boutiqueDisplayName}
          onBack={onBack}
          changeStoreLabel={form.t('changeStore')}
        />
      ) : null}

      <AppointmentFormStepFields
        {...form}
        countryCode={countryCode}
        languageCode={languageCode}
        retailerSection={retailerSection}
      />
    </div>
  );
}

AppointmentFormStep.displayName = 'AppointmentFormStep';
